/**
 * TON Payment Component for Telegram Mini App
 *
 * Integrates TON Connect for cryptocurrency payments.
 * Supports: TON Space (Telegram built-in), Tonkeeper, and other TON wallets.
 *
 * Usage:
 *   import TonPayment from './TonPayment';
 *   <TonPayment planId="vpn_month" onSuccess={() => {}} onError={(err) => {}} />
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTonConnectUI, useTonWallet, TonConnectButton } from '@tonconnect/ui-react';
import { beginCell } from '@ton/core';
import { debugLog } from './config';
import { useTranslation } from './i18n';
import './TonPayment.css';

/**
 * Encode text comment as proper BOC (Bag of Cells) for TON Connect
 * This format works with both Tonkeeper and TON Space
 *
 * @param {string} comment - The comment text (e.g., "VPN-ABC123")
 * @returns {string} - Base64 encoded BOC
 */
function encodeCommentCell(comment) {
  const cell = beginCell()
    .storeUint(0, 32)  // Opcode for text comment
    .storeStringTail(comment)
    .endCell();

  // Convert to base64 BOC
  const boc = cell.toBoc();
  return Buffer.from(boc).toString('base64');
}

/**
 * TON Payment Component
 *
 * Props:
 *   planId: string - Plan ID ('vpn_month' or 'vpn_year')
 *   authData: object - Telegram auth data
 *   onSuccess: () => void - Called when payment is confirmed
 *   onError: (error: string) => void - Called on error
 *   onCancel: () => void - Called when user cancels
 *   apiBase: string - API base URL
 */
function TonPayment({ planId, authData, onSuccess, onError, onCancel, apiBase = '' }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [payment, setPayment] = useState(null);
  const [status, setStatus] = useState('idle'); // idle, creating, waiting, checking, sending, success, error, expired
  const [error, setError] = useState(null);
  const [pollCount, setPollCount] = useState(0);
  const [txSent, setTxSent] = useState(false);
  // Separate polling state from UI status to prevent interruption when user interacts with TON Connect
  const [isPolling, setIsPolling] = useState(false);

  // TON Connect hooks
  const [tonConnectUI] = useTonConnectUI();
  const wallet = useTonWallet();
  const isConnected = !!wallet;

  // Polling interval for checking payment status
  const POLL_INTERVAL = 5000; // 5 seconds
  const MAX_POLLS = 360; // 30 minutes max

  /**
   * Create payment on backend
   */
  const createPayment = useCallback(async () => {
    setLoading(true);
    setStatus('creating');
    setError(null);

    try {
      debugLog('Creating TON payment for plan:', planId);

      const response = await fetch(`${apiBase}/api/telegram/create-ton-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          plan_id: planId,
          auth_data: authData
        }),
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to create payment');
      }

      debugLog('TON payment created:', data);
      setPayment(data);
      setStatus('waiting');
      setPollCount(0);
      setIsPolling(true);  // Start polling for payment confirmation

      return data;

    } catch (err) {
      debugLog('Error creating TON payment:', err);
      setError(err.message);
      setStatus('error');
      onError?.(err.message);
      return null;

    } finally {
      setLoading(false);
    }
  }, [planId, authData, apiBase, onError]);

  /**
   * Check payment status on backend (POST with auth)
   */
  const checkPaymentStatus = useCallback(async (paymentId) => {
    try {
      const response = await fetch(
        `${apiBase}/api/telegram/check-ton-payment`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            auth_data: authData,
            payment_id: paymentId,
          }),
        }
      );

      // Check HTTP status code
      if (!response.ok) {
        debugLog('HTTP error checking payment:', response.status);
        // Auth errors (401) or not found (404) should stop polling
        if (response.status === 401 || response.status === 404) {
          return 'error';
        }
        // Other errors (500, etc.) - continue polling, might be transient
        return 'pending';
      }

      const data = await response.json();

      // Check for application-level errors (success: false)
      if (data.success === false) {
        debugLog('Backend error:', data.error);
        return 'error';
      }

      return data.status || 'pending';

    } catch (err) {
      debugLog('Error checking TON payment status:', err);
      // Network errors might be transient - continue polling
      return 'pending';
    }
  }, [apiBase, authData]);

  // Use ref to avoid recreating interval on every pollCount change
  const pollCountRef = useRef(0);

  /**
   * Poll for payment confirmation
   * Note: isPolling is separated from status to prevent interruption when user
   * interacts with TON Connect (which changes status to 'sending')
   */
  useEffect(() => {
    if (!isPolling || !payment?.payment_id) {
      return;
    }

    debugLog('Starting payment status polling for:', payment.payment_id);
    pollCountRef.current = 0;

    const pollStatus = async () => {
      const paymentStatus = await checkPaymentStatus(payment.payment_id);
      debugLog('Payment status:', paymentStatus, 'poll:', pollCountRef.current);

      if (paymentStatus === 'completed') {
        setIsPolling(false);  // Stop polling
        setStatus('success');
        onSuccess?.();
      } else if (paymentStatus === 'expired') {
        setIsPolling(false);  // Stop polling
        setStatus('expired');
        setError('Payment expired. Please try again.');
        onError?.('Payment expired');
      } else if (paymentStatus === 'error') {
        // Backend returned error - stop polling and notify user
        setIsPolling(false);  // Stop polling
        setStatus('error');
        setError('Payment verification failed. Please try again.');
        onError?.('Payment verification error');
      } else if (paymentStatus === 'not_found') {
        // Payment record doesn't exist - fail fast instead of polling for 30 min
        setIsPolling(false);  // Stop polling
        setStatus('error');
        setError('Payment not found. Please create a new payment.');
        onError?.('Payment not found');
      } else if (pollCountRef.current >= MAX_POLLS) {
        setIsPolling(false);  // Stop polling
        setStatus('expired');
        setError('Payment timeout. Please try again.');
        onError?.('Payment timeout');
      } else {
        pollCountRef.current += 1;
        setPollCount(pollCountRef.current);
      }
    };

    const intervalId = setInterval(pollStatus, POLL_INTERVAL);

    // Initial check after short delay (give time for tx to propagate)
    const timeoutId = setTimeout(pollStatus, 2000);

    return () => {
      clearInterval(intervalId);
      clearTimeout(timeoutId);
    };
  }, [isPolling, payment, checkPaymentStatus, onSuccess, onError]);

  /**
   * Send transaction via TON Connect (works with TON Space and Tonkeeper)
   * Uses proper BOC encoding for the comment payload
   */
  const sendViaTonConnect = useCallback(async () => {
    if (!payment || !isConnected) {
      debugLog('Cannot send: no payment or wallet not connected');
      return;
    }

    setStatus('sending');
    setTxSent(true);

    try {
      debugLog('Sending transaction via TON Connect...');
      debugLog('Amount (nanoTON):', payment.amount_nano);
      debugLog('Comment:', payment.comment);

      // Encode comment as proper BOC using @ton/core
      const payload = encodeCommentCell(payment.comment);
      debugLog('Encoded BOC payload:', payload);

      const transaction = {
        validUntil: Math.floor(Date.now() / 1000) + 600, // 10 minutes
        messages: [
          {
            address: payment.wallet_address,
            amount: String(payment.amount_nano), // Must be string in nanoTON
            payload: payload, // Proper BOC with comment
          },
        ],
      };

      debugLog('Transaction object:', transaction);

      const result = await tonConnectUI.sendTransaction(transaction);
      debugLog('Transaction sent successfully:', result);

      // Transaction sent - switch to waiting for confirmation
      setStatus('waiting');

    } catch (err) {
      debugLog('TON Connect transaction error:', err);

      // User rejected or error occurred
      if (err.message?.includes('User rejected') || err.message?.includes('cancelled')) {
        // User cancelled - go back to waiting state
        setStatus('waiting');
        setTxSent(false);
      } else {
        setError('Transaction failed: ' + (err.message || 'Unknown error'));
        setStatus('error');
        onError?.(err.message);
      }
    }
  }, [payment, isConnected, tonConnectUI, onError]);

  /**
   * Copy wallet address to clipboard
   */
  const copyAddress = useCallback(() => {
    if (payment?.wallet_address) {
      navigator.clipboard.writeText(payment.wallet_address);
      debugLog('Wallet address copied');
      // Show brief feedback
      const btn = document.querySelector('.ton-copyable.address');
      if (btn) {
        btn.classList.add('copied');
        setTimeout(() => btn.classList.remove('copied'), 1000);
      }
    }
  }, [payment]);

  /**
   * Copy payment comment to clipboard
   */
  const copyComment = useCallback(() => {
    if (payment?.comment) {
      navigator.clipboard.writeText(payment.comment);
      debugLog('Payment comment copied');
      const btn = document.querySelector('.ton-copyable.comment');
      if (btn) {
        btn.classList.add('copied');
        setTimeout(() => btn.classList.remove('copied'), 1000);
      }
    }
  }, [payment]);

  /**
   * Open deep link in wallet app
   * Uses Universal Links for better iOS/macOS compatibility
   */
  const openDeepLink = useCallback(() => {
    if (!payment?.deep_link) return;
    
    debugLog('Opening TON deep link:', payment.deep_link);
    
    // Convert ton:// to Universal Link for better iOS/macOS compatibility
    // ton://transfer/ADDRESS?amount=X&text=Y -> https://app.tonkeeper.com/transfer/ADDRESS?amount=X&text=Y
    const tonkeeperLink = payment.deep_link.replace('ton://', 'https://app.tonkeeper.com/');
    
    debugLog('Converted to Tonkeeper Universal Link:', tonkeeperLink);
    
    // Try multiple methods for maximum compatibility
    if (window.Telegram?.WebApp?.openLink) {
      // Telegram Mini App - open in external browser/app
      window.Telegram.WebApp.openLink(tonkeeperLink);
    } else {
      // Fallback - try direct navigation
      window.location.href = tonkeeperLink;
    }
  }, [payment]);

  /**
   * Reset payment state
   */
  const resetPayment = useCallback(() => {
    setStatus('idle');
    setError(null);
    setPayment(null);
    setPollCount(0);
  }, []);

  // Render based on status
  return (
    <div className="ton-payment">
      {/* Status: Idle - Show pay button */}
      {status === 'idle' && (
        <div className="ton-idle">
          <button
            className="ton-pay-button"
            onClick={createPayment}
            disabled={loading}
          >
            💎 {t('tonPayment.title')}
          </button>

          {onCancel && (
            <button className="ton-cancel-button" onClick={onCancel}>
              {t('tonPayment.cancel')}
            </button>
          )}
        </div>
      )}

      {/* Status: Creating - Show loading */}
      {status === 'creating' && (
        <div className="ton-loading">
          <span className="spinner">⏳</span>
          <span>{t('tonPayment.creating')}</span>
        </div>
      )}

      {/* Status: Waiting/Checking/Sending - Show payment details */}
      {(status === 'waiting' || status === 'checking' || status === 'sending') && payment && (
        <div className="ton-payment-details">
          <h3>💎 Pay {payment.amount_ton} TON</h3>

          <div className="ton-info">
            <p><strong>Plan:</strong> {payment.plan_name}</p>
            <p><strong>Network:</strong> {payment.network}</p>
          </div>

          {/* Wallet address */}
          <div className="ton-field">
            <label>Send to address:</label>
            <div className="ton-copyable address" onClick={copyAddress}>
              <code>{payment.wallet_address}</code>
              <span className="copy-icon">📋</span>
            </div>
          </div>

          {/* Comment (IMPORTANT!) */}
          <div className="ton-field ton-important">
            <label>⚠️ Include this comment:</label>
            <div className="ton-copyable comment" onClick={copyComment}>
              <code className="comment-text">{payment.comment}</code>
              <span className="copy-icon">📋</span>
            </div>
            <small>Payment won't be detected without this comment!</small>
          </div>

          {/* Actions */}
          <div className="ton-actions">
            {/* TON Connect: Connect wallet or Send transaction */}
            {!isConnected ? (
              <div className="ton-connect-section">
                <p className="ton-connect-hint">Connect your wallet to pay:</p>
                <TonConnectButton />
                <p className="ton-connect-supported">
                  ✅ TON Space (Telegram) • Tonkeeper • MyTonWallet
                </p>
              </div>
            ) : (
              <button
                className="ton-send-button"
                onClick={sendViaTonConnect}
                disabled={status === 'sending'}
              >
                {status === 'sending' ? '⏳ Sending...' : '💎 Pay via ' + (wallet?.device?.appName || 'Wallet')}
              </button>
            )}

            {/* Alternative: Deep link (for wallets that don't support TON Connect well) */}
            <div className="ton-divider">
              <span>or open wallet manually</span>
            </div>

            {/* Tonkeeper Universal Link */}
            <button
              className="ton-deeplink-button"
              onClick={openDeepLink}
            >
              📱 Open Tonkeeper
            </button>

            {/* Direct ton:// link for other wallets */}
            <a
              href={payment.deep_link}
              className="ton-alt-link"
              target="_blank"
              rel="noopener noreferrer"
            >
              🔗 Other Wallet (ton:// link)
            </a>

            <p className="ton-manual-hint">
              Copy address & comment above for manual transfer
            </p>
          </div>

          {/* Status indicator */}
          <div className="ton-status">
            {status === 'waiting' && !txSent && (
              <span className="status-waiting">
                ⏳ {t('tonPayment.waiting')}
              </span>
            )}
            {status === 'waiting' && txSent && (
              <span className="status-waiting">
                ⏳ {t('tonPayment.checking')} ({Math.floor(pollCount * POLL_INTERVAL / 1000)}s)
              </span>
            )}
            {status === 'sending' && (
              <span className="status-checking">
                📤 {t('tonPayment.connecting')}
              </span>
            )}
          </div>

          {/* Cancel button */}
          <button className="ton-back-button" onClick={resetPayment}>
            ← {t('common.back')}
          </button>
        </div>
      )}

      {/* Status: Success */}
      {status === 'success' && (
        <div className="ton-success">
          <span className="success-icon">✅</span>
          <h3>{t('tonPayment.success')}</h3>
          <p>{t('tonPayment.successDescription')}</p>
          <button className="ton-done-button" onClick={onCancel}>
            {t('common.close')}
          </button>
        </div>
      )}

      {/* Status: Error/Expired */}
      {(status === 'error' || status === 'expired') && (
        <div className="ton-error">
          <span className="error-icon">{status === 'expired' ? '⏰' : '❌'}</span>
          <h3>{status === 'expired' ? t('tonPayment.expired') : t('tonPayment.error')}</h3>
          <p>{error || t('common.error')}</p>
          <button className="ton-retry-button" onClick={resetPayment}>
            {t('common.retry')}
          </button>
          {onCancel && (
            <button className="ton-cancel-button" onClick={onCancel}>
              {t('tonPayment.cancel')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default TonPayment;